import createFetchOptions from './createFetchOptions';
import { getConfig } from './config';
import type RequestOptions from './RequestOptions';
import getGuid from './getGuid';
import { correlationVectorHeaderName, getCorrelationVector } from './correlationVector';
import { getSessionId } from 'owa-config';
import type MailboxRequestOptions from './MailboxRequestOptions';
import type { InternalRequestOptions } from './RequestOptions';
import {
    HEADER_CORRELATIONID,
    HEADER_PROVENANCEID,
    HEADER_SESSIONID,
    HEADER_URLPOSTDATA,
} from './constants/CommonHeaders';
import { getTraceId, isTracingEnabled } from './traceId';

const MAX_HTTP_HEADER_LENGTH = 2048;

export default async function createOptionsForServiceRequest(
    options: RequestOptions | MailboxRequestOptions | undefined,
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional function parameters should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    parameters?: any,
    actionName?: string
): Promise<InternalRequestOptions> {
    const config = getConfig();
    if (config.prepareRequestOptions) {
        const requestOptions = await config.prepareRequestOptions(options);
        if (requestOptions) {
            options = <RequestOptions | MailboxRequestOptions>requestOptions;
        }
    }

    const fetchOptions = await createFetchOptions(options);
    const headers = fetchOptions.headers;

    if (actionName) {
        headers.set('Action', actionName);
    }

    // Add CorrelationId in header, so the FrontEnd-Begin and FrontEnd-End could be stamped in FE server.
    const correlationId = options?.scenarioId ? `${options?.scenarioId}_${getGuid()}` : getGuid();
    headers.set(HEADER_CORRELATIONID, correlationId);
    headers.set(HEADER_SESSIONID, getSessionId());

    if (config.isFeatureEnabled('auth-provenance-OwsAddHeader')) {
        headers.set(HEADER_PROVENANCEID, getGuid());
    }

    if (parameters) {
        headers.append('Content-Type', 'application/json; charset=utf-8');

        const serializedRequestBody = JSON.stringify(parameters);
        const urlEncodedSerializedRequestBody = encodeURIComponent(serializedRequestBody);

        // Check whether to send the payload on the body or on the header
        if (
            fetchOptions.noEmptyPost ||
            urlEncodedSerializedRequestBody.length > MAX_HTTP_HEADER_LENGTH
        ) {
            fetchOptions.body = serializedRequestBody;
        } else {
            // Add empty post marker for logs.
            // endpoint += '&EP=1';

            headers.append(HEADER_URLPOSTDATA, urlEncodedSerializedRequestBody);
            fetchOptions.body = options?.customBody; // Use custom body if provided
        }
    }

    if (!headers.has(correlationVectorHeaderName)) {
        const cv = getCorrelationVector();
        // the cv can be falsy if it gets corrupted, in that case there is no point in sending it
        if (cv) {
            headers.append(correlationVectorHeaderName, cv);
        }
    }

    if (isTracingEnabled(actionName)) {
        const traceId = getTraceId();
        headers.append('traceparent', traceId);
        headers.append('tracestate', 'm365=scenario:owa');
    }

    return fetchOptions;
}
